import React, { useState, useEffect } from "react";
import { AxiosError } from "axios";

import { IEventSummary } from "@eagerdog/interfaces";

import { apiService } from "src/services/api.service";
import { toast } from "src/components/Toast/ToastManager";

import Input from "src/components/Input/Input";
import Button from "src/components/Button/Button";

import styles from "./AssignArmband.module.scss";

interface IProps {
  event: IEventSummary;
  registration: any;
  onComplete: () => void;
  onClose: () => void;
  rowData?: any;
  // When provided, edits only this one run's armband (Entries / Results tab)
  // instead of the whole show/event registration (Registrations tab).
  scoreId?: string;
}

const AssignArmband: React.FC<IProps> = (props) => {
  const [armbandNumber, setArmbandNumber] = useState<string>("");
  const [nextArmbandNumber, setNextArmbandNumber] = useState<string>("");
  const [nextArmbandByGroup, setNextArmbandByGroup] = useState<Array<{
    breed_group: string;
    range_start: number;
    range_end: number;
    next_armband_number: string;
  }>>([]);
  // Per-show-type events (AKC): the number this dog already holds for this show
  // type, and how many runs an assignment will cascade to.
  const [existingArmbandNumber, setExistingArmbandNumber] = useState<string>("");
  const [existingRunsCount, setExistingRunsCount] = useState<number>(0);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isSaving, setIsSaving] = useState<boolean>(false);

  useEffect(() => {
    // Set current armband number if it exists
    if (props.registration) {
      setArmbandNumber(props.rowData?.armband ?? props.registration.arm_number ?? "");
    }
    
    // Get next available armband number
    setIsLoading(true);
    apiService.getNextArmbandNumber(props.event._id, {
      showType: props.registration?.show_type,
      dogId: props.registration?.dog?.dog_id,
    })
      .then((response) => {
        setNextArmbandNumber(response.next_armband_number);
        setNextArmbandByGroup(response.next_armband_numbers_by_group || []);
        setExistingArmbandNumber(response.existing_armband_number || "");
        setExistingRunsCount(response.existing_runs_count || 0);
        // A row with no number yet (e.g. a second trial entered after armbands were
        // generated) starts from the number the dog already has for this show type.
        const rowArmband = props.rowData?.armband ?? props.registration?.arm_number;
        if (!rowArmband && response.existing_armband_number && !props.scoreId) {
          setArmbandNumber(response.existing_armband_number);
        }
      })
      .catch((error: AxiosError) => {
        toast.show({
          title: "Error",
          content: "Unable to get next armband number",
          duration: 5000,
          errorDetails: error,
          type: "fail"
        });
      })
      .finally(() => {
        setIsLoading(false);
      });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.event._id, props.registration.arm_number, props.registration.show_type, props.registration.dog?.dog_id]);

  const showsExistingNumber =
    !props.scoreId && !!existingArmbandNumber;
  const isChangingExisting =
    showsExistingNumber && armbandNumber.trim() !== "" && armbandNumber.trim() !== existingArmbandNumber;

  const handleSave = () => {
    if (!armbandNumber.trim()) {
      toast.show({
        title: "Error",
        content: "Please enter an armband number",
        duration: 5000,
        type: "fail"
      });
      return;
    }

    setIsSaving(true);
    const savePromise = props.scoreId
      ? apiService.assignScoreArmbandNumber(
          props.event._id,
          props.scoreId,
          armbandNumber.trim(),
          props.rowData?.isJuniorDup
        )
      : apiService.assignArmbandNumber(
          props.event._id,
          props.registration.show_id,
          props.registration.dog.dog_id,
          armbandNumber.trim(),
          props.rowData?.isJuniorDup
        );

    savePromise
      .then(() => {
        toast.show({
          title: "Success",
          content: props.scoreId
            ? `Armband number ${armbandNumber} assigned to ${props.registration.dog.call_name} for this run`
            : `Armband number ${armbandNumber} assigned to ${props.registration.dog.call_name}`,
          duration: 5000,
          type: "success"
        });
        props.onComplete();
      })
      .catch((error: AxiosError) => {
        const errorMessage = (error.response?.data as any)?.message || "Unable to assign armband number";
        toast.show({
          title: "Error",
          content: errorMessage,
          duration: 5000,
          errorDetails: error,
          type: "fail"
        });
      })
      .finally(() => {
        setIsSaving(false);
      });
  };

  const handleUseNext = () => {
    if (nextArmbandNumber) {
      setArmbandNumber(nextArmbandNumber);
    }
  };

  const handleUseNextForGroup = (value: string) => {
    if (value) {
      setArmbandNumber(value);
    }
  };

  return (
    <div className={styles.AssignArmband}>
      <div className={styles.header}>
        <h2>Assign / Edit Armband Number</h2>
        <p className={styles.dogInfo}>
          <strong>{props.registration.dog.call_name}</strong> ({props.registration.dog.registered_name})
          <br />
          {props.registration.show_type} {props.registration.show_name}
        </p>
      </div>

      <div className={styles.content}>
        <div className={styles.warningBox}>
          <div className={styles.warningIcon}>⚠️</div>
          <div className={styles.warningText}>
            <strong>Important:</strong> {props.scoreId
              ? "This only changes the armband number for this specific run - it won't affect this dog's other runs or its registration record. "
              : ""}
            Changing an armband number can have effects on the event if exhibitors 
            have already been notified of their previous armband number or if judge's books and scoresheets 
            have already been printed. You may need to print them again and notify exhibitors of the change.
          </div>
        </div>

        {showsExistingNumber && (
          <div className={`${styles.existingArmband} ${isChangingExisting ? styles.existingArmbandChanging : ""}`}>
            {isChangingExisting ? (
              <>
                <strong>Heads up:</strong> {props.registration.dog.call_name} already has armband{" "}
                <strong>#{existingArmbandNumber}</strong> in {props.registration.show_type}. Saving{" "}
                <strong>#{armbandNumber.trim()}</strong> will change it on all {existingRunsCount}{" "}
                {existingRunsCount === 1 ? "run" : "runs"} for this dog in {props.registration.show_type}.
              </>
            ) : (
              <>
                {props.registration.dog.call_name} already has armband <strong>#{existingArmbandNumber}</strong> in{" "}
                {props.registration.show_type} ({existingRunsCount} {existingRunsCount === 1 ? "run" : "runs"}).
                Save to use it for this entry too.
              </>
            )}
          </div>
        )}

        <div className={styles.inputSection}>
          <Input
            label="Armband Number"
            type="text"
            value={armbandNumber}
            onChange={(e) => setArmbandNumber(e.target.value)}
            placeholder="Enter armband number"
            disabled={isSaving}
          />
          
          {nextArmbandByGroup.length > 0 ? (
            <div className={styles.nextArmbandByGroup}>
              <div className={styles.nextArmbandByGroupTitle}>Next available by breed group:</div>
              {nextArmbandByGroup.map((item) => (
                <div key={item.breed_group} className={styles.nextArmbandGroupRow}>
                  <div className={styles.nextArmbandGroupLabel}>
                    {item.breed_group} ({item.range_start}-{item.range_end})
                  </div>
                  <div className={styles.nextArmbandGroupValue}>
                    {item.next_armband_number || "N/A"}
                  </div>
                  <Button
                    onClick={() => handleUseNextForGroup(item.next_armband_number)}
                    disabled={isSaving || !item.next_armband_number}
                    className={styles.useNextButton}
                  >
                    Use Next
                  </Button>
                </div>
              ))}
            </div>
          ) : nextArmbandNumber && (
            <div className={styles.nextArmband}>
              <span className={styles.nextArmbandLabel}>Next available armband:</span>
              <span className={styles.nextArmbandNumber}>{nextArmbandNumber}</span>
              <Button
                onClick={handleUseNext}
                disabled={isSaving}
                className={styles.useNextButton}
              >
                Use Next
              </Button>
            </div>
          )}
        </div>
      </div>

      <div className={styles.footer}>
        <Button
          onClick={props.onClose}
          variant="secondary"
          disabled={isSaving}
          className={styles.cancelButton}
        >
          Cancel
        </Button>
        <Button
          onClick={handleSave}
          disabled={isSaving || isLoading}
          className={styles.saveButton}
        >
          {isSaving ? "Saving..." : "Save"}
        </Button>
      </div>
    </div>
  );
};

export default AssignArmband; 